/**
 * Whether a game's code is available offline, and whether the device is.
 * `PWA-032`, `PWA-033`, `PWA-036`.
 *
 * `PWA-032` requires determining cache status "without fetching" -- SDD-005's
 * own closed question 2 settles how: enumerate `caches.open('pg-game-code').keys()`
 * and match each entry's pathname against the game's own asset prefix, the
 * same `assets/game-<id>-` shape `vite.config.ts`'s `assetFileNames` emits.
 * No build-time id list has to be handed to the shell, and nothing breaks
 * when a chunk's hash changes. `cachedGameIds` is pure and takes the key
 * list as an argument, the same split every other platform module in this
 * project uses (`storage.ts`'s injected `KeyValueStore`,
 * `install.ts`'s `classifyPlatform`), so the matching logic is testable
 * without a real Cache Storage.
 */

import { signal } from '@preact/signals';

/** Exported for `clear-cache.ts`'s own use -- one name, not a second copy
 * that could drift from it. */
export const GAME_CODE_CACHE = 'pg-game-code';

/**
 * `PWA-032`'s pure matching logic. A game's own chunk is named
 * `assets/game-<id>-<hash>.js`. `<id>` (`demo`, `match3`) never contains a
 * hyphen, so it is matched as a hyphen-free run right after `game-`; `<hash>`
 * is not the same guarantee -- Rollup's hash alphabet includes `-` and `_`,
 * so the hash itself can both contain one and end in one (a real build
 * produced `game-match3-BoKi3c_-.js`, found by this stage's own `npm run
 * test:e2e:chromium` run when an unrelated content change happened to land
 * on exactly that hash). The previous pattern required the hash to be
 * hyphen-free, which a hash ending in `-` can never satisfy regardless of
 * where the split falls -- silently emptying the match on any build whose
 * hash happened to end that way, with nothing catching it since no fixture
 * before this stage used a real Rollup hash rather than a hand-picked one.
 */
export function cachedGameIds(cacheKeyPathnames: string[]): Set<string> {
  const ids = new Set<string>();
  for (const pathname of cacheKeyPathnames) {
    const match = /\/assets\/game-([^/-]+)-[^/]+\.js$/.exec(pathname);
    if (match?.[1] !== undefined) ids.add(match[1]);
  }
  return ids;
}

export const cachedGameIdsSignal = signal<Set<string>>(new Set());

/**
 * The live run `cachedGameIds` above stays free of. `caches` is absent in
 * some contexts (private browsing in some browsers) -- the safe default is
 * an empty set, i.e. "nothing is known to be cached", which is what
 * `PWA-033`/`036` already treat as "no offline badge to show" rather than a
 * crash.
 */
export async function refreshCachedGameIds(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const cache = await caches.open(GAME_CODE_CACHE);
    const requests = await cache.keys();
    cachedGameIdsSignal.value = cachedGameIds(requests.map((r) => new URL(r.url).pathname));
  } catch {
    // Cache Storage present but unusable for some other reason -- leave the
    // signal as it was, the same "known-cached" set from before this call,
    // rather than discarding it on a transient read failure.
  }
}

export const isOnline = signal<boolean>(typeof navigator === 'undefined' ? true : navigator.onLine);

export function listenForConnectivityChange(): void {
  window.addEventListener('online', () => {
    isOnline.value = true;
  });
  window.addEventListener('offline', () => {
    isOnline.value = false;
  });
}
